import { useState, useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../services/api';
import {
  useIncomeLineSummary,
  useIncomeLineTree,
  usePatchIncomeLine,
  useDimensionOptions,
  type IncomeLineSummary,
} from '../hooks/useBudgetImport';
import { BudgetTreeView } from '../components/BudgetTreeView';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { useRole, hasMinimumRole } from '../hooks/useRole';

interface IncomeLine {
  InntektslinjeID: string;
  Aar: number;
  Enhet: string;
  Ansvar: string;
  AnsvarNavn: string | null;
  Konto: string;
  KontoNavn: string;
  KontoNavn_dim: string | null;
  SektorID: string | null;
  SektorNavn: string | null;
  RammeomraadeID: string | null;
  RammeomraadeNavn: string | null;
  Tjeneste: string | null;
  TjenesteNavn: string | null;
  Prosjekt: string | null;
  ProsjektNavn: string | null;
  Periodisering: string | null;
  PeriodiseringBeskrivelse: string | null;
  BudsjettBeloep: number | null;
  LinkCount: number;
}

interface PaginatedResponse {
  data: IncomeLine[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalBudsjett: number | null;
  };
}

interface FilterOption {
  SektorID: string;
  SektorNavn: string;
}
interface RammeFilterOption {
  RammeomraadeID: string;
  RammeomraadeNavn: string;
  SektorID: string | null;
}
interface AnsvarFilterOption {
  Ansvar: string;
  AnsvarNavn: string | null;
  RammeomraadeID: string | null;
}
interface FiltersResponse {
  data: {
    sektorer: FilterOption[];
    rammeomraader: RammeFilterOption[];
    ansvar: AnsvarFilterOption[];
  };
}

function formatCurrency(value: number | null): string {
  if (value == null) return '\u2014';
  return value.toLocaleString('nb-NO', { maximumFractionDigits: 0 }) + ' kr';
}

function dekningsgradColor(value: number | null): 'success' | 'warning' | 'error' | 'default' {
  if (value == null) return 'default';
  if (value >= 0.8) return 'success';
  if (value >= 0.5) return 'warning';
  return 'error';
}

function formatPct(value: number | null): string {
  if (value == null) return '\u2014';
  return `${(value * 100).toFixed(0)} %`;
}

// --- Edit dialog ---

interface EditDialogProps {
  row: IncomeLine | null;
  open: boolean;
  onClose: () => void;
}

function EditDialog({ row, open, onClose }: EditDialogProps) {
  const patchMutation = usePatchIncomeLine();
  const tjenesterQuery = useDimensionOptions('tjeneste');
  const prosjektQuery = useDimensionOptions('prosjekt');
  const periodiseringQuery = useDimensionOptions('periodisering');

  const [tjeneste, setTjeneste] = useState<string | null>(null);
  const [prosjekt, setProsjekt] = useState<string | null>(null);
  const [periodisering, setPeriodisering] = useState<string | null>(null);

  // Reset state when a new row is opened
  const handleEntered = useCallback(() => {
    if (row) {
      setTjeneste(row.Tjeneste ?? null);
      setProsjekt(row.Prosjekt ?? null);
      setPeriodisering(row.Periodisering ?? null);
    }
  }, [row]);

  const handleSave = useCallback(async () => {
    if (!row) return;
    await patchMutation.mutateAsync({
      id: row.InntektslinjeID,
      data: { Tjeneste: tjeneste, Prosjekt: prosjekt, Periodisering: periodisering },
    });
    onClose();
  }, [row, tjeneste, prosjekt, periodisering, patchMutation, onClose]);

  const tjenestOptions = (tjenesterQuery.data?.data ?? []) as Array<{ Tjeneste: string; TjenesteNavn: string | null }>;
  const prosjektOptions = (prosjektQuery.data?.data ?? []) as Array<{ Prosjekt: string; ProsjektNavn: string | null }>;
  const periodiseringOptions = (periodiseringQuery.data?.data ?? []) as Array<{ Periodisering: string; Beskrivelse: string | null }>;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth TransitionProps={{ onEntered: handleEntered }}>
      <DialogTitle>
        Rediger dimensjoner
        {row && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {row.Ansvar} — {row.AnsvarNavn ?? row.Ansvar} / konto {row.Konto}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
        {patchMutation.isError && (
          <Alert severity="error">
            {patchMutation.error instanceof Error ? patchMutation.error.message : 'Kunne ikke lagre'}
          </Alert>
        )}

        <Autocomplete
          options={tjenestOptions}
          getOptionLabel={(o) => `${o.Tjeneste}${o.TjenesteNavn ? ` — ${o.TjenesteNavn}` : ''}`}
          value={tjenestOptions.find((o) => o.Tjeneste === tjeneste) ?? null}
          onChange={(_e, v) => setTjeneste(v?.Tjeneste ?? null)}
          loading={tjenesterQuery.isLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Tjeneste"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {tjenesterQuery.isLoading && <CircularProgress size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Autocomplete
          options={prosjektOptions}
          getOptionLabel={(o) => `${o.Prosjekt}${o.ProsjektNavn ? ` — ${o.ProsjektNavn}` : ''}`}
          value={prosjektOptions.find((o) => o.Prosjekt === prosjekt) ?? null}
          onChange={(_e, v) => setProsjekt(v?.Prosjekt ?? null)}
          loading={prosjektQuery.isLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Prosjekt"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {prosjektQuery.isLoading && <CircularProgress size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <FormControl size="small" fullWidth>
          <InputLabel id="periodisering-edit-label">Periodisering</InputLabel>
          <Select
            labelId="periodisering-edit-label"
            value={periodisering ?? ''}
            label="Periodisering"
            onChange={(e) => setPeriodisering(e.target.value || null)}
          >
            <MenuItem value="">(ingen)</MenuItem>
            {periodiseringOptions.map((o) => (
              <MenuItem key={o.Periodisering} value={o.Periodisering}>
                {o.Periodisering}{o.Beskrivelse ? ` — ${o.Beskrivelse}` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={patchMutation.isPending}>Avbryt</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={patchMutation.isPending}
          startIcon={patchMutation.isPending ? <CircularProgress size={16} /> : undefined}
        >
          Lagre
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const lineColumns: GridColDef[] = [
  { field: 'Aar', headerName: 'År', width: 70 },
  { field: 'Ansvar', headerName: 'Ansvar', width: 80 },
  { field: 'AnsvarNavn', headerName: 'Ansvarsnavn', width: 160 },
  { field: 'Konto', headerName: 'Konto', width: 80 },
  {
    field: 'KontoNavn_dim',
    headerName: 'Kontonavn',
    width: 160,
    valueGetter: (_value, row) => row.KontoNavn_dim || row.KontoNavn,
  },
  { field: 'SektorNavn', headerName: 'Sektor', width: 140 },
  { field: 'RammeomraadeNavn', headerName: 'Rammeområde', width: 140 },
  { field: 'TjenesteNavn', headerName: 'Tjeneste', width: 140 },
  { field: 'ProsjektNavn', headerName: 'Prosjekt', width: 140 },
  {
    field: 'BudsjettBeloep',
    headerName: 'Budsjett',
    width: 130,
    type: 'number',
    valueFormatter: (value: number | null) => {
      if (value == null) return '\u2014';
      return (-value).toLocaleString('nb-NO', { maximumFractionDigits: 0 }) + ' kr';
    },
  },
  {
    field: 'LinkCount',
    headerName: 'Avtaler',
    width: 80,
    type: 'number',
    renderCell: (params) => {
      const count = params.value as number;
      if (!count) return '0';
      return (
        <Link
          component={RouterLink}
          to={`/agreements?inntektslinjeID=${encodeURIComponent(params.row.InntektslinjeID)}`}
          underline="hover"
        >
          {count}
        </Link>
      );
    },
  },
];

const summaryColumns: GridColDef[] = [
  { field: 'Ansvar', headerName: 'Ansvar', width: 80 },
  { field: 'AnsvarNavn', headerName: 'Ansvarsnavn', flex: 1, minWidth: 180 },
  { field: 'AntallLinjer', headerName: 'Linjer', width: 80, type: 'number' },
  {
    field: 'TotaltBudsjett',
    headerName: 'Budsjett',
    width: 140,
    type: 'number',
    valueFormatter: (value: number | null) => formatCurrency(value),
  },
  {
    field: 'AntallAvtaler',
    headerName: 'Avtaler',
    width: 80,
    type: 'number',
    renderCell: (params) => {
      const count = params.value as number;
      if (!count) return '0';
      return (
        <Link
          component={RouterLink}
          to={`/agreements?ansvar=${encodeURIComponent(params.row.Ansvar)}`}
          underline="hover"
        >
          {count}
        </Link>
      );
    },
  },
  {
    field: 'RisikojustertDekning',
    headerName: 'Dekning (risikojustert)',
    width: 170,
    type: 'number',
    valueFormatter: (value: number | null) => formatCurrency(value),
  },
  {
    field: 'Dekningsgrad',
    headerName: 'Dekningsgrad',
    width: 130,
    renderCell: (params) => (
      <Chip
        label={formatPct(params.value)}
        size="small"
        color={dekningsgradColor(params.value)}
      />
    ),
  },
];

export function IncomeLinesPage() {
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'lines' | 'summary' | 'tree'>('tree');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [sektorId, setSektorId] = useState('');
  const [rammeomraadeId, setRammeomraadeId] = useState('');
  const [ansvar, setAnsvar] = useState('');
  const [editRow, setEditRow] = useState<IncomeLine | null>(null);
  const role = useRole();
  const canEdit = hasMinimumRole(role, 'Editor');

  const columnsWithEdit = useMemo<GridColDef[]>(() => {
    if (!canEdit) return lineColumns;
    const editCol: GridColDef = {
      field: '__edit__',
      headerName: '',
      width: 52,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          aria-label="Rediger dimensjoner"
          onClick={() => setEditRow(params.row as IncomeLine)}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      ),
    };
    return [...lineColumns, editCol];
  }, [canEdit]);

  // Fetch filter options
  const filtersQuery = useQuery({
    queryKey: ['income-lines-filters'],
    queryFn: () => apiGet<FiltersResponse>('/income-lines/filters'),
    staleTime: 5 * 60 * 1000,
  });

  // Cascading filter: rammeområder filtered by selected sektor
  const filteredRammeomraader = useMemo(() => {
    const all = filtersQuery.data?.data?.rammeomraader ?? [];
    if (!sektorId) return all;
    return all.filter((r) => r.SektorID === sektorId);
  }, [filtersQuery.data, sektorId]);

  // Cascading filter: ansvar filtered by selected rammeområde
  const filteredAnsvar = useMemo(() => {
    const all = filtersQuery.data?.data?.ansvar ?? [];
    if (rammeomraadeId) return all.filter((a) => a.RammeomraadeID === rammeomraadeId);
    if (sektorId) {
      const rammeIds = new Set(filteredRammeomraader.map((r) => r.RammeomraadeID));
      return all.filter((a) => a.RammeomraadeID && rammeIds.has(a.RammeomraadeID));
    }
    return all;
  }, [filtersQuery.data, sektorId, rammeomraadeId, filteredRammeomraader]);

  const queryKey = ['income-lines', search, sektorId, rammeomraadeId, ansvar, paginationModel.page, paginationModel.pageSize];

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('page', String(paginationModel.page + 1));
      params.set('pageSize', String(paginationModel.pageSize));
      if (search) params.set('search', search);
      if (sektorId) params.set('sektorId', sektorId);
      if (rammeomraadeId) params.set('rammeomraadeId', rammeomraadeId);
      if (ansvar) params.set('ansvar', ansvar);
      return apiGet<PaginatedResponse>(`/income-lines?${params}`);
    },
    enabled: view === 'lines',
  });

  const summaryQuery = useIncomeLineSummary(undefined, sektorId || undefined);
  const treeQuery = useIncomeLineTree();

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const totalCount = data?.pagination?.totalCount ?? 0;
  const totalBudsjett = data?.pagination?.totalBudsjett ?? null;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h2">Inntektslinjer</Typography>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_e, v) => v && setView(v)}
          size="small"
        >
          <ToggleButton value="lines">Per linje</ToggleButton>
          <ToggleButton value="summary">Per ansvar</ToggleButton>
          <ToggleButton value="tree"><AccountTreeIcon sx={{ fontSize: 18, mr: 0.5 }} /> Hierarki</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Totals bar — lines view only */}
      {view === 'lines' && data && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2">
            <strong>{totalCount}</strong> inntektslinjer
          </Typography>
          {totalBudsjett != null && (
            <Typography variant="body2">
              Totalt budsjett: <strong>{formatCurrency(totalBudsjett)}</strong>
            </Typography>
          )}
        </Paper>
      )}

      {/* Filters — hidden in tree view */}
      {view !== 'tree' && (
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {view === 'lines' && (
          <TextField
            size="small"
            placeholder="Søk ansvar, konto, navn..."
            value={search}
            onChange={handleSearchChange}
            sx={{ width: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon /></InputAdornment>
              ),
            }}
          />
        )}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="sektor-filter-label">Sektor</InputLabel>
          <Select
            labelId="sektor-filter-label"
            value={sektorId}
            label="Sektor"
            onChange={(e) => {
              setSektorId(e.target.value);
              setRammeomraadeId('');
              setAnsvar('');
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
          >
            <MenuItem value="">Alle</MenuItem>
            {(filtersQuery.data?.data?.sektorer ?? []).map((s) => (
              <MenuItem key={s.SektorID} value={s.SektorID}>{s.SektorNavn}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="ramme-filter-label">Rammeområde</InputLabel>
          <Select
            labelId="ramme-filter-label"
            value={rammeomraadeId}
            label="Rammeområde"
            onChange={(e) => {
              setRammeomraadeId(e.target.value);
              setAnsvar('');
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
          >
            <MenuItem value="">Alle</MenuItem>
            {filteredRammeomraader.map((r) => (
              <MenuItem key={r.RammeomraadeID} value={r.RammeomraadeID}>
                {r.RammeomraadeID} — {r.RammeomraadeNavn}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="ansvar-filter-label">Ansvar</InputLabel>
          <Select
            labelId="ansvar-filter-label"
            value={ansvar}
            label="Ansvar"
            onChange={(e) => {
              setAnsvar(e.target.value);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
          >
            <MenuItem value="">Alle</MenuItem>
            {filteredAnsvar.map((a) => (
              <MenuItem key={a.Ansvar} value={a.Ansvar}>
                {a.Ansvar} — {a.AnsvarNavn || a.Ansvar}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      )}

      {view === 'lines' && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : 'Kunne ikke laste inntektslinjer'}
        </Alert>
      )}

      {view === 'summary' && summaryQuery.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {summaryQuery.error instanceof Error ? summaryQuery.error.message : 'Kunne ikke laste oppsummering'}
        </Alert>
      )}

      {view === 'tree' && treeQuery.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {treeQuery.error instanceof Error ? treeQuery.error.message : 'Kunne ikke laste hierarki'}
        </Alert>
      )}

      {view === 'lines' && (
        <DataGrid
          rows={data?.data ?? []}
          columns={columnsWithEdit}
          getRowId={(row) => row.InntektslinjeID}
          loading={isLoading}
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          autoHeight
          sx={{ bgcolor: 'background.paper' }}
        />
      )}

      {view === 'summary' && (
        <DataGrid
          rows={summaryQuery.data?.data ?? []}
          columns={summaryColumns}
          getRowId={(row) => row.Ansvar}
          loading={summaryQuery.isLoading}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
          disableRowSelectionOnClick
          autoHeight
          sx={{ bgcolor: 'background.paper' }}
        />
      )}

      {view === 'tree' && (
        <BudgetTreeView
          rows={treeQuery.data?.data ?? []}
          loading={treeQuery.isLoading}
        />
      )}

      <EditDialog
        row={editRow}
        open={editRow !== null}
        onClose={() => setEditRow(null)}
      />
    </Box>
  );
}
